import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ShieldCheck,
  Loader2,
  Building2,
  Mail,
  CheckCircle2,
  AlertTriangle,
  ArrowLeft,
  LogIn,
  UserPlus,
  Eye,
  Users,
  UserCog,
} from "lucide-react";
import { supabase } from "../supabaseClient";

type InvitationRow = {
  id: string;
  club_id: string;
  email: string;
  role: string;
  token: string;
  created_at: string;
  accepted_at: string | null;
  expires_at: string | null;
};

type ClubRow = {
  id: string;
  name: string;
  city: string | null;
  country: string | null;
  logo_path: string | null;
};

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function formatDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

const ROLE_CONFIG: Record<
  string,
  {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    accentBox: string;
    infoBox: string;
    successMessage: string;
    description: string;
  }
> = {
  assistant: {
    label: "Cadre technique",
    icon: ShieldCheck,
    accentBox:
      "bg-purple-100 text-purple-900 dark:bg-purple-900/20 dark:text-purple-200",
    infoBox:
      "rounded-2xl border border-purple-200 bg-purple-50 px-4 py-4 text-sm text-purple-900 dark:border-purple-900/40 dark:bg-purple-900/10 dark:text-purple-200",
    successMessage:
      "Invitation acceptée. Tu fais maintenant partie du club comme cadre technique.",
    description:
      "En tant que cadre technique, tu pourras gérer les équipes et les données sportives du club, à l’exception des commissions.",
  },
  coach: {
    label: "Coach",
    icon: UserCog,
    accentBox:
      "bg-blue-100 text-blue-900 dark:bg-blue-900/20 dark:text-blue-200",
    infoBox:
      "rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4 text-sm text-blue-900 dark:border-blue-900/40 dark:bg-blue-900/10 dark:text-blue-200",
    successMessage:
      "Invitation acceptée. Tu fais maintenant partie du club comme coach.",
    description:
      "En tant que coach, tu rejoindras le staff du club avec des accès adaptés à ton périmètre d’équipe.",
  },
  admin: {
  label: "Dirigeant",
  icon: Users,
  accentBox:
    "bg-yellow-100 text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-200",
  infoBox:
    "rounded-2xl border border-yellow-200 bg-yellow-50 px-4 py-4 text-sm text-yellow-900 dark:border-yellow-900/40 dark:bg-yellow-900/10 dark:text-yellow-200",
  successMessage:
    "Invitation acceptée. Tu fais maintenant partie du club comme dirigeant.",
  description:
    "En tant que dirigeant, tu auras un accès complet aux données du club et à son administration.",
},
  joueur: {
  label: "Joueur",
  icon: Users,
  accentBox:
    "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-200",
  infoBox:
    "rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-900/10 dark:text-emerald-200",
  successMessage:
    "Invitation acceptée. Tu fais maintenant partie du club comme joueur.",
  description:
    "En tant que joueur, tu pourras accéder aux informations et fonctionnalités autorisées pour les joueurs du club.",
},
  manager: {
    label: "Dirigeant",
    icon: Users,
    accentBox:
      "bg-yellow-100 text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-200",
    infoBox:
      "rounded-2xl border border-yellow-200 bg-yellow-50 px-4 py-4 text-sm text-yellow-900 dark:border-yellow-900/40 dark:bg-yellow-900/10 dark:text-yellow-200",
    successMessage:
      "Invitation acceptée. Tu fais maintenant partie du club comme dirigeant.",
    description:
      "En tant que dirigeant, tu auras un accès complet aux données du club et à son administration.",
  },
  viewer: {
    label: "Lecteur",
    icon: Eye,
    accentBox:
      "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-200",
    infoBox:
      "rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4 text-sm text-gray-700 dark:border-gray-800 dark:bg-gray-900/40 dark:text-gray-200",
    successMessage:
      "Invitation acceptée. Tu fais maintenant partie du club comme lecteur.",
    description:
      "En tant que lecteur, tu auras un accès en lecture aux informations du club autorisées pour ton rôle.",
  },
  player_parent: {
    label: "Parent",
    icon: Users,
    accentBox:
      "bg-green-100 text-green-900 dark:bg-green-900/20 dark:text-green-200",
    infoBox:
      "rounded-2xl border border-green-200 bg-green-50 px-4 py-4 text-sm text-green-900 dark:border-green-900/40 dark:bg-green-900/10 dark:text-green-200",
    successMessage:
      "Invitation acceptée. Tu fais maintenant partie du club comme parent.",
    description:
      "En tant que parent, tu pourras consulter les informations liées à l’équipe de ton enfant une fois le rattachement configuré.",
  },
};

const DEFAULT_ROLE_CONFIG = {
  label: "Membre",
  icon: ShieldCheck,
  accentBox:
    "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-200",
  infoBox:
    "rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4 text-sm text-gray-700 dark:border-gray-800 dark:bg-gray-900/40 dark:text-gray-200",
  successMessage: "Invitation acceptée.",
  description:
    "Tu es invité à rejoindre le club avec un rôle configuré par l’administrateur.",
};

export default function AcceptClubInvitation() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [invitation, setInvitation] = useState<InvitationRow | null>(null);
  const [club, setClub] = useState<ClubRow | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!token) {
        setErrorMsg("Lien d’invitation invalide.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErrorMsg(null);
        setSuccessMsg(null);

        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (!cancelled) {
          setUserEmail(user?.email ?? null);
          setUserId(user?.id ?? null);
        }

        const { data, error } = await supabase.rpc("get_public_club_invitation", {
          _token: token,
        });

        if (error) throw error;
        if (!data) throw new Error("Invitation introuvable.");

        if (!cancelled) {
          setInvitation({
            id: data.id,
            club_id: data.club_id,
            email: data.email,
            role: data.role,
            token: data.token,
            created_at: data.created_at,
            accepted_at: data.accepted_at,
            expires_at: data.expires_at,
          });

          setClub({
            id: data.club.id,
            name: data.club.name,
            city: data.club.city,
            country: data.club.country,
            logo_path: data.club.logo_path,
          });
        }
      } catch (err: any) {
        if (!cancelled) {
          setErrorMsg(err?.message || "Impossible de charger l’invitation.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const emailMatches = useMemo(() => {
    if (!invitation?.email || !userEmail) return false;
    return invitation.email.toLowerCase() === userEmail.toLowerCase();
  }, [invitation?.email, userEmail]);

  const loginRedirect = `/login?redirect=${encodeURIComponent(`/invite/${token}`)}`;
  const signupRedirect = `/signup?redirect=${encodeURIComponent(`/invite/${token}`)}`;

  const roleConfig = invitation?.role
    ? ROLE_CONFIG[invitation.role] ?? DEFAULT_ROLE_CONFIG
    : DEFAULT_ROLE_CONFIG;

  const RoleIcon = roleConfig.icon;

  async function handleAccept() {
    if (!token || !userId || !emailMatches) return;

    setAccepting(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const { data, error } = await supabase.rpc("accept_club_invitation", {
        _token: token,
      });

      if (error) throw error;

      setSuccessMsg(roleConfig.successMessage);

      const nextClubId = data?.club_id || invitation?.club_id;
      if (nextClubId) {
        setTimeout(() => {
          navigate(`/club/${nextClubId}/members`);
        }, 1000);
      }
    } catch (err: any) {
      setErrorMsg(err?.message || "Impossible d’accepter l’invitation.");
    } finally {
      setAccepting(false);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <div className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin" />
            Chargement de l’invitation…
          </div>
        </div>
      </div>
    );
  }

  if (errorMsg && !invitation) {
    return (
      <div className="mx-auto max-w-2xl p-6 space-y-4">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </Link>

        <div className="rounded-3xl border border-red-200 dark:border-red-900/40 bg-white dark:bg-gray-900 p-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-red-500 mt-0.5" />
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Invitation invalide
              </h1>
              <p className="mt-2 text-sm text-red-700 dark:text-red-200">{errorMsg}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl p-6 space-y-6">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:underline"
      >
        <ArrowLeft className="h-4 w-4" />
        Retour
      </Link>

      <div className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 md:p-8">
        <div className="flex items-start gap-4">
          <div className={cx("rounded-2xl p-3", roleConfig.accentBox)}>
            <RoleIcon className="h-6 w-6" />
          </div>

          <div className="min-w-0">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Invitation club
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Invitation {roleConfig.label}
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Tu es invité à rejoindre ce club avec le rôle de{" "}
              <span className="font-medium">{roleConfig.label}</span>.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 md:p-8 space-y-5">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              <Building2 className="h-4 w-4" />
              Club
            </div>
            <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              <div className="font-semibold">{club?.name ?? "—"}</div>
              <div className="text-gray-500 dark:text-gray-400">
                {[club?.city, club?.country].filter(Boolean).join(", ") ||
                  "Localisation non renseignée"}
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              <Mail className="h-4 w-4" />
              Email invité
            </div>
            <div className="mt-2 text-sm text-gray-700 dark:text-gray-300 break-all">
              {invitation?.email}
            </div>
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Invitation créée le {formatDate(invitation?.created_at)}
            </div>
          </div>
        </div>

        <div className={roleConfig.infoBox}>{roleConfig.description}</div>

        {!userId && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-200">
            Cette invitation est destinée à{" "}
            <span className="font-medium">{invitation?.email}</span>. Connecte-toi
            avec cette adresse ou crée un compte pour continuer.
          </div>
        )}

        {userId && emailMatches && (
          <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-4 text-sm text-green-700 dark:border-green-900/40 dark:bg-green-900/10 dark:text-green-200">
            Tu es connecté avec la bonne adresse email :{" "}
            <span className="font-medium">{userEmail}</span>.
          </div>
        )}

        {userId && !emailMatches && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-200">
            Tu es connecté avec <span className="font-medium">{userEmail}</span>, mais
            cette invitation est destinée à{" "}
            <span className="font-medium">{invitation?.email}</span>.
            <div className="mt-2">
              Déconnecte-toi puis reconnecte-toi avec la bonne adresse, ou crée un
              compte avec cet email.
            </div>
          </div>
        )}

        {errorMsg && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-200">
            {errorMsg}
          </div>
        )}

        {successMsg && (
          <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-900/40 dark:bg-green-900/10 dark:text-green-200">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              {successMsg}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          {!userId ? (
            <>
              <Link
                to={loginRedirect}
                className="inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
              >
                <LogIn className="h-4 w-4" />
                Se connecter
              </Link>

              <Link
                to={signupRedirect}
                className="inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <UserPlus className="h-4 w-4" />
                Créer un compte
              </Link>
            </>
          ) : (
            <button
              type="button"
              onClick={handleAccept}
              disabled={!emailMatches || accepting}
              className={cx(
                "inline-flex items-center justify-center rounded-2xl px-4 py-3 text-sm font-medium",
                "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
                (!emailMatches || accepting) && "opacity-60 cursor-not-allowed"
              )}
            >
              {accepting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Acceptation...
                </>
              ) : (
                `Accepter l’invitation ${roleConfig.label.toLowerCase()}`
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}